import { Action, createReducer, on } from "@ngrx/store";

import { Category, Menu } from "@hermes/api-model-menu";

import {
    fetchMenu,
    fetchMenuFailure,
    fetchMenuSuccess,
} from "../actions/menu.action";

import { flattenMenuToCategories } from "../helpers/categories.helper";
import {
    extractLinksFromAboutEntries,
    extractLinksFromMenuEntries,
} from "../helpers/entry.helper";
import { FlatCategories, MenuLink } from "../model/menu.model";

export const menuFeatureKey = "menu_discovery";

export interface State {
    entry: Menu | undefined;
    categories: FlatCategories | undefined;
    links: MenuLink[] | undefined;
    pending: boolean;
}

export const initialState: State = {
    entry: undefined,
    categories: undefined,
    links: undefined,
    pending: false,
};

const menuReducer = createReducer(
    initialState,
    on(fetchMenu, fetchMenuReducer),
    on(fetchMenuSuccess, fetchMenuSuccessReducer),
    on(fetchMenuSuccess, extractCategoriesReducer),
    on(fetchMenuFailure, fetchMenuFailureReducer),
);

export function reducer(state: State | undefined, action: Action): State {
    return menuReducer(state, action);
}

export function fetchMenuReducer(
    state: State,
    _action: ReturnType<typeof fetchMenu>,
): State {
    return {
        ...state,
        pending: true,
    };
}

function filterVisibleCategories(
    categories: Category[] | undefined,
): Category[] | undefined {
    if (!categories) {
        return undefined;
    }

    return categories
        .filter((cat) => cat.visibility)
        .map((cat) => {
            if (filterVisibleCategories(cat.subcategories)) {
                return {
                    ...cat,
                    subcategories: filterVisibleCategories(cat.subcategories),
                };
            }
            return cat;
        });
}

export function fetchMenuSuccessReducer(
    state: State,
    action: ReturnType<typeof fetchMenuSuccess>,
): State {
    const mainMenuVisible: Category[] | undefined = filterVisibleCategories(
        action.entry.mainMenu,
    );

    const extractMenuEntries = extractLinksFromMenuEntries(mainMenuVisible);

    const extractAboutEntries = extractLinksFromAboutEntries(
        action.entry.aboutMenu,
    );

    const mergedLinks = [...extractMenuEntries, ...extractAboutEntries];

    return {
        ...state,
        entry: {
            ...action.entry,
            mainMenu: mainMenuVisible,
        },
        links: mergedLinks,
        pending: false,
    };
}

export function fetchMenuFailureReducer(
    state: State,
    _action: ReturnType<typeof fetchMenuFailure>,
): State {
    return {
        ...state,
        pending: false,
    };
}

export function extractCategoriesReducer(
    state: State,
    action: ReturnType<typeof fetchMenuSuccess>,
): State {
    return {
        ...state,
        categories: flattenMenuToCategories(action.entry.mainMenu),
    };
}
